import { JSONObject } from '../../types/types';
import { hasStringPlaceholder } from './hasStringPlaceholder';
import {
  CustomJsonataFunctions,
  resolveStringExpression,
} from './resolveStringExpression';
import { debugLogger } from '../../debug/DebugLogger';
import { PLACEHOLDER_PATTERN } from '../../constants';

const log = debugLogger.scope('resolveExpressions');

/**
 * Resolves JSONata placeholders inside a string against the provided data.
 * Keeps the original JSON type only when the whole string is a single placeholder.
 *
 * Optional `customFunctions` are forwarded to `resolveStringExpression` so
 * callers can expose extra `$name(...)` bindings (e.g. `$apiLookup`).
 */
export const resolveStringPlaceholders = async (
  value: string,
  jsonData: JSONObject,
  customFunctions?: CustomJsonataFunctions
) => {
  if (!hasStringPlaceholder(value)) {
    return value;
  }

  const placeholderMatches = Array.from(value.matchAll(PLACEHOLDER_PATTERN));

  if (placeholderMatches.length === 0) {
    return value;
  }

  // Keep JSON type for pure placeholders like "{{ pricewit }}".
  if (placeholderMatches.length === 1 && placeholderMatches[0][0] === value) {
    return await resolveStringExpression(
      placeholderMatches[0][1],
      jsonData,
      customFunctions
    );
  }

  const resolvePartPromises = placeholderMatches.map(async (match) => {
    const resolvedValue = await resolveStringExpression(
      match[1],
      jsonData,
      customFunctions
    );

    return {
      expression: match[0],
      // String interpolation always produces text for mixed content placeholders.
      value: resolvedValue == null ? '' : String(resolvedValue),
    };
  });
  const resolvedParts = await Promise.all(resolvePartPromises);

  const result = resolvedParts.reduce<string>(
    (resolvedString, resolvedPart) => {
      return resolvedString.replace(
        resolvedPart.expression,
        resolvedPart.value
      );
    },
    value
  );

  /** @debugFeature resolveExpressions Logs a mixed-content string template with multiple placeholders: the original template and the final interpolated string. */
  log('interpolated string template', () => ({ template: value, result }));

  return result;
};
